import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, X, Clock, CreditCard } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';

const DISMISSED_KEY = 'trial_banner_dismissed';

export function TrialBanner() {
  const { tenant, user } = useAuth();
  const [isDismissed, setIsDismissed] = useState(false);

  // Check if dismissed today
  useEffect(() => {
    const dismissedDate = localStorage.getItem(DISMISSED_KEY);
    if (dismissedDate) {
      const today = new Date().toDateString();
      if (dismissedDate === today) {
        setIsDismissed(true);
      }
    }
  }, []);

  // Not a trial tenant or already dismissed
  if (!tenant || tenant.plan !== 'trial' || !tenant.trial_ends_at || isDismissed) {
    return null;
  }

  const trialEndsAt = new Date(tenant.trial_ends_at);
  const now = new Date();
  const daysRemaining = Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const isExpired = daysRemaining <= 0;
  const isExpiringSoon = daysRemaining > 0 && daysRemaining <= 3;

  const handleDismiss = () => {
    const today = new Date().toDateString();
    localStorage.setItem(DISMISSED_KEY, today);
    setIsDismissed(true);
  };

  // Determine banner style based on trial status
  let bannerClass = 'bg-blue-50 border-blue-200 text-blue-800';
  let Icon = Clock;
  let message = `Your trial ends in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`;

  if (isExpired) {
    bannerClass = 'bg-red-50 border-red-200 text-red-800';
    Icon = AlertTriangle;
    message = 'Your trial has expired. Upgrade now to continue using SecureSend.';
  } else if (isExpiringSoon) {
    bannerClass = 'bg-yellow-50 border-yellow-200 text-yellow-800';
    Icon = AlertTriangle;
    message = daysRemaining === 1
      ? 'Your trial expires tomorrow!'
      : `Your trial expires in ${daysRemaining} days`;
  }

  return (
    <div className={cn('border-b px-4 py-2 flex items-center justify-between', bannerClass)}>
      <div className="flex items-center gap-3">
        <Icon className="h-4 w-4 shrink-0" />
        <span className="text-sm font-medium">{message}</span>
      </div>
      <div className="flex items-center gap-2">
        {user?.role === 'admin' && (
          <Link
            to="/app/settings?tab=billing"
            className={cn(
              'inline-flex items-center px-3 py-1 text-xs font-medium rounded-md transition-colors',
              isExpired
                ? 'bg-red-600 text-white hover:bg-red-700'
                : isExpiringSoon
                  ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
            )}
          >
            <CreditCard className="h-3 w-3 mr-1" />
            Upgrade Now
          </Link>
        )}
        {!isExpired && (
          <button
            onClick={handleDismiss}
            className="p-1 rounded-sm hover:bg-black/10 transition-colors"
            title="Dismiss for today"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
